import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";

// 20-page album booklet as an interactive two-page spread reader.
// Mobile: single-page swipe. Desktop (>= 768px): two-page spread flip.
// Front cover (index 0) and back cover (index 19) show alone on desktop
// to preserve the natural feel of opening/closing a real booklet.
//
// Controls:
//   • Prev / Next buttons
//   • Left / Right arrow keys (when reader is focused)
//   • Touch swipe (mobile)
//   • Click a page to enter fullscreen lightbox for that spread
//   • Escape closes lightbox

const PAGES = Array.from({ length: 20 }, (_, i) => {
  if (i === 0) return "/assets/booklet/album-booklet-00-front-cover.jpg";
  if (i === 19) return "/assets/booklet/album-booklet-19-back-cover.jpg";
  return `/assets/booklet/album-booklet-${String(i).padStart(2, "0")}.jpg`;
});

// Given the current spread index and viewport mode, return the pages shown.
// Spread rhythm on desktop (spread index → visible pages):
//   0 → [cover]           (front cover alone)
//   1 → [1, 2]
//   2 → [3, 4]
//   ...
//   9 → [17, 18]
//   10 → [back]           (back cover alone)
// Total: 11 desktop spreads (0..10). On mobile: 20 single pages (0..19).
function pagesForSpread(spreadIndex, isDesktop) {
  if (!isDesktop) return [spreadIndex];
  if (spreadIndex === 0) return [0];
  if (spreadIndex === 10) return [19];
  const left = spreadIndex * 2 - 1;
  return [left, left + 1];
}

const DESKTOP_TOTAL = 11;
const MOBILE_TOTAL = 20;

export default function BookletViewer() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true,
  );
  const [spread, setSpread] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [direction, setDirection] = useState(1);
  const touchStartX = useRef(null);
  const readerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => {
      setIsDesktop(e.matches);
      setSpread(0); // reset to cover when layout swaps
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const total = isDesktop ? DESKTOP_TOTAL : MOBILE_TOTAL;
  const visible = useMemo(() => pagesForSpread(spread, isDesktop), [spread, isDesktop]);

  const go = useCallback(
    (delta) => {
      setSpread((s) => {
        const next = Math.max(0, Math.min(total - 1, s + delta));
        setDirection(delta > 0 ? 1 : -1);
        return next;
      });
    },
    [total],
  );

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (lightbox) {
        if (e.key === "Escape") setLightbox(false);
        else if (e.key === "ArrowRight") go(1);
        else if (e.key === "ArrowLeft") go(-1);
        return;
      }
      // Only respond when the reader has focus (avoid hijacking page scroll)
      if (!readerRef.current) return;
      if (!readerRef.current.contains(document.activeElement)) return;
      if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [go, lightbox]);

  // Touch swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const atStart = spread === 0;
  const atEnd = spread === total - 1;

  return (
    <div data-testid="booklet-viewer" className="w-full">
      {/* Intro / kicker */}
      <div className="mb-6 md:mb-8 max-w-3xl">
        <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#F4C10A] mb-2">
          20 Pages · Original 2003 Booklet
        </div>
        <p className="font-body text-white/70 text-base leading-relaxed">
          Turn every page of the original Philadelphia Freeway booklet. Tap
          the expand icon on any spread to view full-size.
        </p>
      </div>

      {/* Reader stage */}
      <div
        ref={readerRef}
        tabIndex={0}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="relative bg-neutral-950 border border-white/10 focus:outline-none focus:ring-1 focus:ring-[#F4C10A]/60 select-none"
        data-testid="booklet-stage"
      >
        {/* Page counter */}
        <div className="absolute top-3 left-3 z-10 font-mono text-[10px] tracking-[0.3em] uppercase text-white/60 bg-black/50 px-2 py-1">
          <span data-testid="booklet-counter">
            {isDesktop
              ? atStart
                ? "Cover"
                : atEnd
                ? "Back Cover"
                : `Pages ${visible[0]} – ${visible[1]}`
              : `Page ${spread + 1} / ${MOBILE_TOTAL}`}
          </span>
        </div>

        {/* Fullscreen button */}
        <button
          data-testid="booklet-fullscreen"
          onClick={() => setLightbox(true)}
          aria-label="View full size"
          className="absolute top-3 right-3 z-10 bg-black/55 hover:bg-black/80 text-white/90 hover:text-[#F4C10A] p-2 transition-colors"
        >
          <Maximize2 className="w-4 h-4" />
        </button>

        {/* Pages */}
        <div className="relative w-full flex items-center justify-center p-4 md:p-8 min-h-[380px] md:min-h-[520px] overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={spread}
              initial={{ opacity: 0, x: direction * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -40 }}
              transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
              className={`w-full flex ${visible.length === 2 ? "gap-2 md:gap-3" : "justify-center"}`}
            >
              {visible.map((pageIdx, i) => (
                <button
                  key={pageIdx}
                  onClick={() => setLightbox(true)}
                  data-testid={`booklet-page-${pageIdx}`}
                  aria-label={`Booklet page ${pageIdx}`}
                  className={`relative group ${
                    visible.length === 2 ? "flex-1 min-w-0" : "w-full max-w-[520px]"
                  }`}
                >
                  <img
                    src={PAGES[pageIdx]}
                    alt={`Album booklet page ${pageIdx}`}
                    loading={Math.abs(pageIdx - visible[0]) <= 1 ? "eager" : "lazy"}
                    className="block w-full h-auto object-contain shadow-lg shadow-black/40 border border-white/5"
                  />
                  {/* Inner shadow to hint at the center gutter for two-page spread */}
                  {visible.length === 2 && (
                    <div
                      aria-hidden="true"
                      className={`pointer-events-none absolute inset-y-0 ${
                        i === 0 ? "right-0 w-6" : "left-0 w-6"
                      }`}
                      style={{
                        background:
                          i === 0
                            ? "linear-gradient(to left, rgba(0,0,0,0.35), rgba(0,0,0,0))"
                            : "linear-gradient(to right, rgba(0,0,0,0.35), rgba(0,0,0,0))",
                      }}
                    />
                  )}
                </button>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Prev / Next controls */}
        <button
          data-testid="booklet-prev"
          onClick={() => go(-1)}
          disabled={atStart}
          aria-label="Previous page"
          className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-[#F4C10A] hover:text-black text-white p-2 md:p-3 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black/60 disabled:hover:text-white"
        >
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        <button
          data-testid="booklet-next"
          onClick={() => go(1)}
          disabled={atEnd}
          aria-label="Next page"
          className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-[#F4C10A] hover:text-black text-white p-2 md:p-3 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black/60 disabled:hover:text-white"
        >
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
        </button>
      </div>

      {/* Progress dots (desktop only, one per spread) */}
      <div className="mt-5 flex justify-center gap-1.5" data-testid="booklet-progress">
        {Array.from({ length: total }).map((_, i) => (
          <button
            key={i}
            onClick={() => {
              setDirection(i > spread ? 1 : -1);
              setSpread(i);
            }}
            aria-label={`Go to spread ${i + 1}`}
            className={`h-1.5 transition-all ${
              i === spread ? "bg-[#F4C10A] w-6" : "bg-white/25 hover:bg-white/50 w-3"
            }`}
          />
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            data-testid="booklet-lightbox"
            role="dialog"
            aria-modal="true"
            onClick={() => setLightbox(false)}
            className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-md flex items-center justify-center p-3 md:p-8"
          >
            <button
              data-testid="booklet-lightbox-close"
              aria-label="Close full size"
              onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/25 text-white p-2"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              disabled={atStart}
              aria-label="Previous"
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-[#F4C10A] hover:text-black text-white p-3 transition-colors disabled:opacity-30"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go(1); }}
              disabled={atEnd}
              aria-label="Next"
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-[#F4C10A] hover:text-black text-white p-3 transition-colors disabled:opacity-30"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <div
              onClick={(e) => e.stopPropagation()}
              className={`flex ${visible.length === 2 ? "gap-2 md:gap-3" : "justify-center"} max-w-[95vw] max-h-[90vh]`}
            >
              {visible.map((pageIdx) => (
                <img
                  key={pageIdx}
                  src={PAGES[pageIdx]}
                  alt={`Album booklet page ${pageIdx} full size`}
                  className="block max-h-[90vh] w-auto object-contain"
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
